// Angular Core
import { Component, ChangeDetectorRef, OnDestroy, inject, ViewChild, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// Utilities
import { parseMarkdown } from 'src/app/utils/markdown.utils';

// RxJS
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';

// Siemens UI Components
import { SiContentActionBarModule } from '@siemens/element-ng/content-action-bar';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { StatusType } from '@siemens/element-ng/common';
import { SiModalService } from '@siemens/element-ng/modal';
import { SiSplitComponent, SiSplitPartComponent } from '@siemens/element-ng/split';
import { SiChatInputComponent, ChatInputAttachment, MessageAction, SiAttachmentListComponent, Attachment } from '@siemens/element-ng/chat-messages';
import { SiTooltipDirective } from '@siemens/element-ng/tooltip';
import { SiIconComponent } from '@siemens/element-ng/icon';

// Application Services
import { ChatService } from 'src/app/services/chat.service';
import { StreamingService } from 'src/app/services/streaming.service';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { UserSettingsService } from 'src/app/services/user-settings.service';
import { TokenUsageService } from 'src/app/services/token-usage.service';

// Application Exceptions
import {
  StreamingException,
  StreamingAuthenticationException,
  StreamingConnectionException,
  StreamingAbortedException
} from 'src/app/exceptions/streaming-exceptions';

// Application Components
import { TabsetComponent } from 'src/app/shared/components/tabset/tabset.component';
import { EditorAutomationHubComponent } from './editor/editor-automation-hub/editor-automation-hub.component';
import { EditorSupportHubComponent } from './editor/editor-support-hub/editor-support-hub.component';
import { InsightsComponent } from './results/insights/insights.component';
import { FeedComponent } from './results/feed/feed.component';
import { ChatCopilotComponent } from './chat/chat-copilot/chat-copilot.component';
import { FeedbackModalComponent } from 'src/app/components/feedback-modal/feedback-modal.component';
import { FeedbackModalData } from 'src/app/interfaces/feedback.interface';

// Application Interfaces
import { IMessage, IStreamingMessageV2, IFileAttachment } from 'src/app/interfaces/streaming-messages.interface';
import { CodeEditorMessage } from 'src/app/interfaces/code-editor-message.interface';
import { TabModel } from 'src/app/interfaces/tab-model-interface';
import { ThreadContent, ThreadResponse } from 'src/app/interfaces/thread-response.interface';

// Application Constants
import { EDITOR_TABS } from 'src/app/constants/editor-tabs';
import { RESULT_TABS } from 'src/app/constants/result-tabs';
import { CHAT_TABS } from 'src/app/constants/chat-tabs';

// Application Utils
import { formatMessageWithLineBreaks, renderStructuredResult } from 'src/app/utils/execution-result.utils';
// Runtime config
import { AppConfigService } from 'src/app/services/app-config.service';

@Component({
  selector: 'app-chat-enhanced',
  imports: [
    CommonModule, FormsModule, SiContentActionBarModule, SiSplitComponent, SiSplitPartComponent,
    TabsetComponent, EditorAutomationHubComponent, EditorSupportHubComponent,
    InsightsComponent, FeedComponent, ChatCopilotComponent, SiChatInputComponent, SiAttachmentListComponent,
    SiTooltipDirective, SiIconComponent],
  templateUrl: './chat-enhanced.component.html',
  styleUrl: './chat-enhanced.component.scss'
})
export class ChatEnhancedComponent implements OnDestroy {
  private readonly modalService = inject(SiModalService);

  @ViewChild('middleBottom') middleBottomSplit?: SiSplitPartComponent;
  @ViewChild('chatPanel') chatPanelSplit?: SiSplitPartComponent;
  isResultsCollapsed = false;
  isChatCollapsed = false;
  splitReady = true;
  readonly userSettingsService = inject(UserSettingsService);
  readonly tokenUsageService = inject(TokenUsageService);
  readonly toggleResultsPanel = () => this.middleBottomSplit?.toggleCollapse();
  readonly toggleChatPanel = () => this.chatPanelSplit?.toggleCollapse();
  editorTabs: TabModel[] = [...EDITOR_TABS];
  resultTabs: TabModel[] = [...RESULT_TABS];
  chatTabs: TabModel[] = [...CHAT_TABS];
  currentEditorTabIndex = 0;
  currentResultTabIndex = 0;
  currentChatTabIndex = 0;

  constructor(
    private readonly chatService: ChatService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toastNotificationService: SiToastNotificationService,
    private readonly route: ActivatedRoute,
    private readonly streamingService: StreamingService,
    private readonly fileUploadService: FileUploadService,
    private readonly configService: AppConfigService
  ) {
    // Check if running in Electron
    this.isElectronMode = !!(window as any).psse;

    // Initialize attach file action for Electron mode
    this.attachFileAction = this.isElectronMode ? [{
      label: 'Attach PSS®E files',
      icon: 'element-upload',
      action: () => this.onAttachFileAction()
    }] : [];

    this.route.params.subscribe((paramVal) => {
      this.threadId = paramVal["threadId"];
      if (this.threadId) {
        // Destroy and recreate the inner si-split so its fractionalSize signal
        // is reset. Without this, dragging the result panel to 0 leaves the
        // gutter stuck at the top after switching threads.
        this.isResultsCollapsed = false;
        this.splitReady = false;
        setTimeout(() => {
          this.splitReady = true;
          this.cdr.markForCheck();
        }, 0);
        this.loadThreadData(this.threadId);
        this.loadThreadFiles(this.threadId);
        this.tokenUsageService.load();
      }
    });

    // Load PSSE versions
    this.loadPsseVersions();

    // When insights is turned off while the Insights tab is active, switch to Feed
    effect(() => {
      if (!this.userSettingsService.settings().insights_enabled && this.currentResultTabIndex === 0) {
        this.currentResultTabIndex = 1;
        this.cdr.markForCheck();
      }
    });
  }

  threadId: string = '';
  messageId: string = '';
  inputMessageText = '';
  executeCommandList: IMessage[] = [];
  isStreaming: boolean = false;
  finalCode: string = '';
  streamingData: IMessage | null = null;
  codeEditorData: CodeEditorMessage = {
    finalResponse: undefined,
    finalExecutedResult: '',
    isFinalCodeExecuting: false
  };
  isUserSubmitted = false;
  isHistoricalReplay = false;

  insightsContent: string = '';

  // File attachment properties
  attachedFiles: IFileAttachment[] = [];
  isElectronMode = false;
  chatAttachments: ChatInputAttachment[] = [];
  attachFileAction: MessageAction[] = [];

  // PSSE Version properties
  psseVersions: string[] = [];
  selectedPsseVersion: string = '';
  defaultPsseVersion: string = '';
  isPsseDropdownDisabled: boolean = false;
  currentPythonVersion: string = ''; // Store python version from backend

  isElectron = (window as any).psse?.isElectron;

  // Template helper used to decide whether code execution result should be shown
  get isCodeExecuted(): boolean {
    const val = this.codeEditorData?.finalExecutedResult;
    return typeof val === 'string' ? val.trim().length > 0 : false;
  }

  // Event handlers for action buttons
  handleCopyAction = () => {
    if (this.currentEditorTabIndex === 0) {
      this.copy(this.finalCode);
    } else if (this.currentEditorTabIndex === 1) {
      this.copySupportHubContent();
    }
  };

  handleExecuteAction = () => {
    if (this.codeEditorData.isFinalCodeExecuting) return;
    this.isUserSubmitted = true;
    this.validateAndExecuteCode(this.finalCode);
  };

  onResultsCollapseChanged(collapsed: boolean): void {
    this.isResultsCollapsed = collapsed;
  }

  onChatCollapseChanged(collapsed: boolean): void {
    this.isChatCollapsed = collapsed;
  }

  private copySupportHubContent(): void {
    const completedCommands = this.executeCommandList.filter(
      cmd => cmd.currentStreamingState === 'run_complete'
    );
    const formattedText = completedCommands
      .map(cmd => {
        const heading = cmd.inputMessage ?? '';
        const content = this.getContentFromCommand(cmd);
        return `## "${heading}"\n\n${content}`;
      })
      .join('\n\n---\n\n');
    this.copy(formattedText);
  }

  private getContentFromCommand(cmd: IMessage): string {
    const streamingMatch = (cmd.streamingMessages ?? []).find(
      (msg: IStreamingMessageV2) =>
        msg.content_type === 'text' &&
        msg.content !== null &&
        typeof msg.content === 'object' &&
        'text' in msg.content
    );
    return (streamingMatch?.content as any)?.text ?? cmd.response ?? '';
  }

  // Handle code changes from automation hub
  validateAndExecuteCode(code: string, streamType: string = ""): void {

    this.currentResultTabIndex = 1; // Switch to Feed tab on execution

    const isAgentInterrupted = streamType === 'run_waiting' ? true : false;
    if (!this.messageId) {
      this.isUserSubmitted = false;
      return;
    }
    this.codeEditorData.isFinalCodeExecuting = true;
    this.cdr.detectChanges();

    // Call backend to validate AND get filename-replaced code
    // Pass execution_mode so backend knows whether to execute (browser) or just validate (electron)
    const executionMode = this.isElectronMode ? 'electron' : 'browser';
    this.chatService.executeCode(code, this.threadId, this.messageId, false, executionMode, isAgentInterrupted).pipe(
      tap(response => {
        if (!response.success || !response.validation?.valid) {
          // Validation failed - show errors
          const errors = response.validation?.errors || [];
          const errorMsg = errors.map((e: any) =>
            `Line ${e.line || '?'}: ${e.message}`
          ).join('\n');

          this.codeEditorData.finalResponse = parseMarkdown(`\`\`\`\nValidation Failed:\n${errorMsg}\n\`\`\``);
          this.codeEditorData.isFinalCodeExecuting = false;
          this.cdr.detectChanges();
        } else {
          // Validation passed - use modified code from backend (with replaced filenames)
          const modifiedCode = response.modified_code || code;
          const pythonVersion = response.python_version; // Get python version from backend

          // Store python version for future streaming executions
          this.currentPythonVersion = pythonVersion;

          if (this.isElectronMode) {
            // Standalone Run Execution in Desktop mode with modified code
            this.executeRunInDesktop(modifiedCode, streamType, pythonVersion);
          } else {
            // Browser mode standalone execution
            const executionResult = response.result.messages ? response.result.messages : "No response";
            if (streamType === 'run_waiting') {
              this.resumeWorkflow(executionResult)
              this.codeEditorData.isFinalCodeExecuting = false;
            } else {
              this.codeEditorData.finalResponse = parseMarkdown(`\`\`\`\n${executionResult}\n\`\`\``);
              this.codeEditorData.isFinalCodeExecuting = false;
              this.cdr.detectChanges();
              this.executeInsights(executionResult);
            }
          }

        }
      }),
      catchError(error => {
        this.codeEditorData.isFinalCodeExecuting = false;
        this.showToast('danger', 'Error', 'Validation failed');
        this.cdr.detectChanges();
        return of(null);
      })
    ).subscribe();
  }

  executeRunInDesktop(code: string, streamType: string, pythonVersion: string): void {
    // Run Execution flow: Execute in Electron + PSSE (standalone, not workflow)

    this.chatService.executeRun(this.threadId, code, pythonVersion)
      .then(executionResult => {
        if (streamType === 'run_waiting') {
          this.resumeWorkflow(executionResult)
          this.codeEditorData.isFinalCodeExecuting = false;
          this.cdr.detectChanges();
        } else {
          this.chatService.submitRunExecutionResult(this.threadId, executionResult).pipe(
            tap(response => {
              this.codeEditorData.finalResponse = renderStructuredResult(response);
              this.codeEditorData.isFinalCodeExecuting = false;
              this.cdr.detectChanges();
            }),
            catchError(error => {
              this.codeEditorData.isFinalCodeExecuting = false;
              this.showToast('danger', 'Error', 'Failed to process execution result');
              this.cdr.detectChanges();
              return of(null);
            })
          ).subscribe();

          this.executeInsights(executionResult);
        }

      })
      .catch(error => {
        this.codeEditorData.finalResponse = parseMarkdown(`\`\`\`\nExecution Error:\n${error}\n\`\`\``);
        this.codeEditorData.isFinalCodeExecuting = false;
        this.showToast('danger', 'Execution Failed', 'Failed to execute code in PSSE');
        this.cdr.detectChanges();
      });
  }

  // Handle code changes from child code editor component
  onFinalCodeChange(newCode: string): void {
    this.finalCode = newCode;
  }

  updateEditorTabsActive(index: number): void {
    this.editorTabs = this.editorTabs.map((tab, i) => ({
      ...tab,
      active: i === index
    }));
  }


  /** Hides the Insights tab entirely when the setting is off. Active state is derived from currentResultTabIndex. */
  get displayResultTabs(): TabModel[] {
    const insightsEnabled = this.userSettingsService.settings().insights_enabled;
    const tabs = insightsEnabled ? this.resultTabs : this.resultTabs.filter(t => t.heading !== 'Insights');
    // When Insights is hidden, Feed is at visible index 0 but real index 1
    const visibleActiveIndex = insightsEnabled ? this.currentResultTabIndex : 0;
    return tabs.map((tab, i) => ({ ...tab, active: i === visibleActiveIndex }));
  }

  loadThreadData(threadId: string): void {
    // Logic to load data based on threadId
    this.resetStreamData();
    this.resetCodeEditorData();
    this.chatService.getThreadById(threadId).pipe(
      tap((response: ThreadResponse) => {
        // Set PSSE version from thread (if available)
        if (response?.psse_version) {
          this.selectedPsseVersion = response.psse_version;
        } else if (this.defaultPsseVersion) {
          // Use default if thread has no version set
          this.selectedPsseVersion = this.defaultPsseVersion;
        }

        // Disable dropdown only if thread has messages AND already has a psse_version set
        // Allow setting version on old threads that don't have one yet
        const hasMessages = response?.content && response.content.length > 0;
        this.isPsseDropdownDisabled = hasMessages && !!response?.psse_version;

        if (response?.content && response.content.length > 0) {
          this.executeCommandList = response.content.map((item: ThreadContent) => {
            const command: IMessage = {
              messageId: item.message_id,
              inputMessage: item.question,
              displayInputMessage: formatMessageWithLineBreaks(item.question),
              code: item.code,
              isShowExecuteCodeLoader: false,
              sendMessageError: '',
              feedback: item.feedback ?? null,
              isStreaming: false,
              currentStreamingState: 'run_complete',
              text: item.text ?? '',
              streamingMessages: [],
              response: item.response ?? ''
            };


            // Process streaming_events if they exist for this item
            if (item.streaming_events?.length) {
              this.processHistoricalStreamingEvents(command, item.streaming_events);
            }
            this.streamingData = command;
            this.messageId = this.streamingData?.messageId ?? ''; // Store message ID for insights execution
            return command;
          });
        } else {
          this.executeCommandList = [];
        }

        this.scrollToBottom();
      }),
      catchError(error => {
        return of(null);
      })
    ).subscribe();
  }


  private processHistoricalStreamingEvents(command: IMessage, streamingEvents: IStreamingMessageV2[]): void {
    const lastEvent = streamingEvents[streamingEvents.length - 1] as IStreamingMessageV2 | undefined;
    const lastEventIsWaiting = lastEvent?.kind === 'control' && lastEvent?.phase === 'waiting';

    this.isHistoricalReplay = true;
    streamingEvents.forEach(streamEvent => {
      this.handleStreamingData(command, streamEvent);
    });
    this.isHistoricalReplay = false;

    // After replaying all historical events, ensure the state reflects only the last event.
    // If the last event was NOT a waiting control message, reset to run_complete so a
    // mid-history "waiting" event doesn't leave the UI stuck in run_waiting.
    if (!lastEventIsWaiting) {
      command.currentStreamingState = 'run_complete';
    }
  }

  resetCodeEditorData() {
    this.codeEditorData = {
      finalResponse: undefined,
      finalExecutedResult: '',
      isFinalCodeExecuting: false
    };
  }

  async sendMessage(): Promise<void> {
    const inputMessage = this.inputMessageText.trim();
    if (inputMessage === '') {
      return;
    } else {
      // Disable PSSE dropdown once first message is sent
      if (this.executeCommandList.length === 0) {
        this.isPsseDropdownDisabled = true;
      }
      const command: IMessage = {
        inputMessage: inputMessage,
        displayInputMessage: formatMessageWithLineBreaks(inputMessage),
        code: '',
        text: '',
        isShowExecuteCodeLoader: false,
        sendMessageError: '',
        isStreaming: false,
        streamingMessages: [],
      };
      this.executeCommandList = [...this.executeCommandList, command];
      this.isUserSubmitted = false;
      this.inputMessageText = '';
      // Start workflow first to get threadId
      await this.runWorkflow(command);


    }
  }

  async runWorkflow(command: IMessage): Promise<void> {
    command.currentStreamingState = 'progress';

    try {
      // Attach files BEFORE starting workflow so the agent has file context
      if (this.attachedFiles.length > 0 && this.threadId) {
        await this.attachFilesToThread();
      }

      // Start workflow (agent can now query attached files from DB by thread_id)
      const response = await this.chatService.startWorkflow(
        command.inputMessage,
        this.threadId,
        this.selectedPsseVersion || undefined
      ).toPromise();
      // Update thread ID from backend if provided
      if (response && response.thread_id) {
        this.chatService.setSharedThreadId(response.thread_id);
        this.threadId = response.thread_id;
        this.messageId = response.message_id; // Store message ID for future reference (like submitting execution result)
      }

      // Start streaming if thread ID is available
      if (this.threadId) {
        command.messageId = response.message_id;
        await this.startStreaming(command);
      }
    } catch (error: any) {
      this.chatService.setSharedThreadId(this.threadId);
      const status = error?.status ?? error?.statusCode;
      if (status === 429) {
        const detail = error?.error?.detail ?? 'Monthly token limit exceeded. Please try again next month.';
        command.sendMessageError = detail;
        this.showToast('warning', 'Token Limit Exceeded', detail);
      } else {
        this.showToast('danger', 'Error', 'Please try again');
        command.sendMessageError = 'Error sending message, Please try again...';
      }

      command.currentStreamingState = 'run_complete';
      command.isStreaming = false;
      this.isStreaming = false;
    }
  }

  async startStreaming(command: IMessage): Promise<void> {
    command.isStreaming = true;
    this.isStreaming = true;
    command.streamingMessages = [];

    // Connect to streaming service
    const baseUrl = this.configService.apiBaseUrl;
    const streamUrl = `${baseUrl}/api/v2/workflow/stream/${this.threadId}`;
    await this.streamingService.connect<IStreamingMessageV2>(
      streamUrl,
      (streamData) => {
        this.handleStreamingData(command, streamData);
      },
      (error: StreamingException) => {
        this.handleStreamingError(command, error);
      }
    );
  }

  private handleStreamingError(command: IMessage, error: StreamingException): void {
    command.isStreaming = false;
    this.isStreaming = false;

    // Log error with full context
    console.error('Streaming error occurred:', {
      errorType: error.name,
      errorCode: error.code,
      message: error.message,
      details: error.details,
      threadId: this.threadId,
      messageId: command.messageId,
      timestamp: new Date().toISOString()
    });

    // Handle specific error types with appropriate messages
    if (error instanceof StreamingAuthenticationException) {
      command.sendMessageError = `Authentication failed. Please refresh and try again.`;
    } else if (error instanceof StreamingConnectionException) {
      // I dont want to show this message
      command.sendMessageError = `Network connection lost. Please check your connection and try again.`;
    } else if (error instanceof StreamingAbortedException) {
      command.sendMessageError = `Streaming was cancelled.`;
    } else {
      // Generic error handling for unknown exceptions
      command.sendMessageError = `Error during streaming. Please try again.`;
    }
  }

  private handleStreamingData(command: IMessage, streamData: IStreamingMessageV2): void {
    // SECURITY (Issue 6): Validate that the stream event belongs to the current session.
    // Rejects events whose metadata thread_id does not match the active threadId,
    // mitigating cross-session injection via compromised or replayed SSE streams.
    const eventThreadId = (streamData as any).metadata?.thread_id;
    if (eventThreadId && eventThreadId !== this.threadId) {
      console.warn(`[SECURITY] Stream event thread_id mismatch: expected=${this.threadId}, got=${eventThreadId}. Event discarded.`);
      return;
    }

    const data = streamData as IStreamingMessageV2;
    try {
      command.streamingMessages = command.streamingMessages ?? [];

      this.processControlMessage(command, data);
      this.processContentMessage(data);
      this.processStatusMessage(command, data);
    } catch (error) {
      // Log error but don't break the streaming
    }

    this.addStreamingMessageToUI(command, data);
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  private processControlMessage(command: IMessage, streamData: IStreamingMessageV2): void {
    // Handle control messages (like waiting for user input)
    if (streamData.kind === 'control') {
      command.currentStreamingState = 'run_waiting';
      // Only execute code for live streaming, not when replaying historical events
      if (!this.isHistoricalReplay) {
        const code = streamData.content ? streamData.content : ""
        this.validateAndExecuteCode(code, 'run_waiting');
      }
    }
  }


  private processContentMessage(streamData: IStreamingMessageV2): void {
    // Capture final code and response from content messages (only when content is an object)
    if (streamData.kind === 'content' && this.isValidContentObject(streamData)) {
      this.handleCodeContent(streamData);
      this.handleTextContent(streamData);
    }
    if (streamData.kind === 'content' && streamData.content_type === 'insight' && typeof streamData.content === 'string') {
      this.insightsContent = parseMarkdown(streamData.content);
      if (!this.isHistoricalReplay) {
        this.isUserSubmitted = true;
      }
    }
  }

  private isValidContentObject(streamData: IStreamingMessageV2): boolean {
    return streamData.content !== undefined && typeof streamData.content === 'object';
  }

  private handleCodeContent(streamData: IStreamingMessageV2): void {
    // Handle content_type === 'code'
    if (streamData.content_type === 'code' && streamData.content && typeof streamData.content === 'object' && 'code' in streamData.content) {
      this.currentEditorTabIndex = 0; // Switch to Automation Hub tab
      this.updateEditorTabsActive(0);
      this.finalCode = (streamData.content as any).code;
    }
  }

  private handleTextContent(streamData: IStreamingMessageV2): void {
    // Handle content_type === 'text'
    if (streamData.content_type === 'text' && streamData.content && typeof streamData.content === 'object' && 'text' in streamData.content) {
      this.currentEditorTabIndex = 1; // Switch to Support Hub tab
      this.updateEditorTabsActive(1);
    }
  }

  private processStatusMessage(command: IMessage, streamData: IStreamingMessageV2): void {
    // Close SSE connection when workflow is completed (but don't show this message in UI)
    if (streamData.kind === 'status' && streamData.phase === 'completed') {
      command.isStreaming = false;
      this.isStreaming = false;
      command.currentStreamingState = 'run_complete';
      this.streamingService.disconnect();
      this.executeCommandList = [...this.executeCommandList];
      this.tokenUsageService.load();
    }
  }

  private addStreamingMessageToUI(command: IMessage, streamData: IStreamingMessageV2): void {
    // Filter out status messages with 'started' or 'completed' phases from UI display
    if (this.shouldSkipStatusMessage(streamData)) {
      return;
    }

    const streamingMessageV2: IStreamingMessageV2 = {
      kind: streamData.kind,
      phase: streamData.phase,
      content_type: streamData.content_type,
      content: streamData.content,
      message: streamData.message,
      metadata: streamData.metadata,
      timestamp: streamData.timestamp
    };
    command.streamingMessages = command.streamingMessages ?? [];
    command.streamingMessages.push(streamingMessageV2);
  }

  private shouldSkipStatusMessage(streamData: IStreamingMessageV2): boolean {
    return streamData.kind === 'status' &&
      (streamData.phase === 'started' || streamData.phase === 'completed');
  }


  resumeWorkflow(executedResult: string): void {

    this.chatService.submitExecutionResult(this.threadId, this.messageId, executedResult).pipe(
      tap(response => {
        // response handled silently
      }),
      catchError(error => {
        // Error handling - log and swallow for tests
        this.codeEditorData.isFinalCodeExecuting = false;
        this.cdr.detectChanges();
        return of(null);
      })
    ).subscribe();
  }

  executeInsights(executionResult: string): void {
    if (!this.userSettingsService.settings().insights_enabled) {
      return;
    }
    this.insightsContent = '';
    const messageId = this.streamingData?.messageId ?? this.messageId;
    this.chatService.executeInsights(this.finalCode, this.threadId, messageId, executionResult).pipe(
      tap(() => this.connectInsightsStream(messageId)),
      catchError(error => {
        this.codeEditorData.isFinalCodeExecuting = false;
        this.cdr.detectChanges();
        return of(null);
      })
    ).subscribe();
  }

  private connectInsightsStream(messageId: string): void {
    this.chatService.connectInsightsStream(
      this.threadId,
      messageId,
      (event) => this.handleInsightsEvent(event),
      (error) => this.handleStreamingError({} as IMessage, error)
    );
  }

  private handleInsightsEvent(event: IStreamingMessageV2): void {
    if (event.kind === 'content' && event.content_type === 'insight') {
      this.insightsContent = parseMarkdown(event.content ?? '');
      this.currentResultTabIndex = 0; // Switch to Insights tab on execution
      this.isUserSubmitted = true;
      this.cdr.detectChanges();
    }
    if (event.kind === 'status' && event.phase === 'completed') {
      this.handleInsightsCompletion();
    }
  }

  private handleInsightsCompletion(): void {
    this.codeEditorData.isFinalCodeExecuting = false;
    this.streamingService.disconnect();
    this.cdr.detectChanges();
  }

  scrollToBottom(): void {
    setTimeout(() => {
      // Try to scroll to the thread container bottom or page bottom
      const threadContainer = document.querySelector('.thread-container');
      if (threadContainer) {
        threadContainer.scrollTop = threadContainer.scrollHeight;
      } else {
        // Fallback: scroll to bottom of page
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  }

  sendFeedback(cmd: IMessage, value: 'up' | 'down') {
    // Validate required fields
    if (!cmd.messageId) {
      // message id missing -- log and return
      return;
    }
    if (!this.threadId) {
      // thread id missing -- log and return
      return;
    }

    // For thumbs up, submit directly without showing modal
    if (value === 'up') {
      this.submitFeedbackToBackend(cmd, value, '', '');
      return;
    }

    // For thumbs down, show the feedback modal
    const modalData: FeedbackModalData = {
      feedbackType: value,
      command: cmd
    };

    const modalRef = this.modalService.show(
      FeedbackModalComponent,
      {
        inputValues: modalData as unknown as Record<string, unknown>,
        ignoreBackdropClick: false,
        keyboard: true,
        animated: true,
        class: 'modal-dialog-centered',
        ariaLabelledBy: 'feedback-modal-title'
      },
      'cancel'
    );

    modalRef.hidden.subscribe(result => {
      if (result && result !== 'cancel' && typeof result === 'object') {
        // User submitted feedback with message and category
        const feedbackResult = result as { type: 'up' | 'down'; message: string; category?: string };
        this.submitFeedbackToBackend(cmd, feedbackResult.type, feedbackResult.message, feedbackResult.category);
      }
    });
  }

  private submitFeedbackToBackend(cmd: IMessage, feedbackType: 'up' | 'down', feedbackMessage: string, feedbackCategory?: string) {
    // Update the command feedback state
    cmd.messageId = cmd.messageId ?? '';
    cmd.feedback = feedbackType;
    cmd.feedbackMessage = feedbackMessage; // Update the feedback message as well
    cmd.feedbackCategory = feedbackCategory ?? ''; // Store the category as well

    this.chatService.sendFeedback(cmd.messageId, feedbackType, this.threadId, feedbackMessage, feedbackCategory)
      .subscribe({
        next: (res: unknown) => {
          const message = feedbackType === 'up'
            ? 'Your feedback has been submitted successfully.'
            : 'Your feedback helps us improve.';
          this.showToast('success', 'Thank you!', message);
        },
        error: (err: unknown) => {
          this.showToast('danger', 'Error', 'Failed to submit feedback. Please try again.');
        }
      });
  }

  copy(code: string) {
    navigator.clipboard.writeText(code).then(() => {
      this.showToast('success', 'Success', 'Code copied to clipboard');
    }).catch(err => {
      this.showToast('danger', 'Error', 'Could not copy code to clipboard');
    });
  }

  showToast(
    state: StatusType,
    title: string,
    message: string,
  ): void {
    const toast = this.toastNotificationService.showToastNotification({
      state,
      title,
      message
    });
    // Do not log toast hide events in production; no-op subscription kept for side-effects
    toast.hidden?.subscribe(() => { });
  }

  onEditorTabChanged(index: number): void {
    if (index !== 0) {
      // Reset collapsed state so it syncs with the fresh SiSplitPartComponent
      // instance that is recreated when switching back to the Automation tab.
      this.isResultsCollapsed = false;
    }
    this.currentEditorTabIndex = index;
  }

  onResultTabChanged(index: number): void {
    // When Insights is hidden, the only visible tab (Feed) emits index 0 but its real index is 1
    this.currentResultTabIndex = !this.userSettingsService.settings().insights_enabled ? 1 : index;
  }

  // File attachment methods
  async attachFiles(): Promise<void> {
    if (!this.fileUploadService.isElectronAvailable()) {
      this.showToast('warning', 'Not Available', 'File selection is only available in Electron app');
      return;
    }

    try {
      const result = await this.fileUploadService.selectAndCopyFiles(this.attachedFiles, this.threadId);

      // Handle errors
      for (const error of result.errors) {
        if (error.type === 'validation') {
          this.showToast('danger', 'Invalid File Type', error.message);
        } else if (error.type === 'duplicate') {
          this.showToast('warning', 'Already Attached', error.message);
        } else if (error.type === 'copy') {
          this.showToast('danger', 'Copy Failed', error.message);
        } else {
          this.showToast('danger', 'Error', error.message);
        }
      }

      // Add successfully copied files
      const newChatAttachments: ChatInputAttachment[] = [];
      for (const file of result.addedFiles) {
        this.attachedFiles = [...this.attachedFiles, file];

        // Create a File object for ChatInputAttachment
        const mockFile = new File([], file.name, { type: file.type });
        newChatAttachments.push({
          file: mockFile,
          name: file.name,
          size: 0,
          type: file.type
        });
      }
      if (newChatAttachments.length > 0) {
        this.chatAttachments = [...this.chatAttachments, ...newChatAttachments];
      }

      if (result.filesAdded > 0) {
        const message = this.fileUploadService.getFileSelectionSuccessMessage(result.filesAdded);
        this.showToast('success', 'Files Attached', message);
      }

      this.cdr.detectChanges();
    } catch (error) {
      this.showToast('danger', 'Error', 'Failed to select files');
    }
  }

  async removeFile(attachment: ChatInputAttachment | Attachment): Promise<void> {
    const index = this.chatAttachments.findIndex(a => a.name === attachment.name);
    if (index >= 0 && index < this.attachedFiles.length) {
      const file = this.attachedFiles[index];

      // Delete from backend if it has an attachmentId
      const result = await this.fileUploadService.removeFile(file);

      if (!result.success) {
        this.showToast('danger', 'Error', result.error || 'Failed to remove file from thread');
        return;
      }

      // Show success message only if file had backend ID
      if (file.attachmentId) {
        this.showToast('success', 'File Removed', `${result.fileName} removed from thread`);
      }

      // Remove from both arrays (new references so Angular detects the change)
      this.attachedFiles = this.attachedFiles.filter((_, i) => i !== index);
      this.chatAttachments = this.chatAttachments.filter((_, i) => i !== index);
      this.cdr.detectChanges();
    }
  }

  async loadThreadFiles(threadId: string): Promise<void> {
    const result = await this.fileUploadService.loadThreadFiles(threadId);

    if (result.error) {
      console.error('Error loading thread files:', result.error);
      return;
    }

    this.attachedFiles = result.files;

    // Populate chatAttachments for si-chat-input
    this.chatAttachments = this.attachedFiles.map(file => {
      const mockFile = new File([], file.name, { type: file.type });
      return {
        file: mockFile,
        name: file.name,
        size: 0,
        type: file.type
      } as ChatInputAttachment;
    });

    this.cdr.detectChanges();
  }

  async attachFilesToThread(): Promise<void> {
    try {
      await this.fileUploadService.attachFilesToThread(this.attachedFiles, this.threadId);
    } catch (error: any) {
      this.showToast('danger', 'Error', error.message || 'Failed to attach files');
    }
  }

  onFileError(error: any): void {
    this.showToast('danger', 'File Error', error.message || 'Failed to upload file');
  }

  onAttachFileAction(): void {
    this.attachFiles();
  }

  // PSSE Version Management
  loadPsseVersions(): void {
    this.chatService.getPSSEVersions().pipe(
      tap(response => {
        this.psseVersions = response.versions || [];
        this.defaultPsseVersion = response.default_version || '';
        // Set default as selected if no thread version loaded yet
        if (!this.selectedPsseVersion && this.defaultPsseVersion) {
          this.selectedPsseVersion = this.defaultPsseVersion;
        }
        this.cdr.markForCheck();
      }),
      catchError(error => {
        console.error('Failed to load PSSE versions:', error);
        return of(null);
      })
    ).subscribe();
  }

  onPsseVersionChange(version: string): void {
    // Just update the local selected version
    // It will be sent with the first message via /start endpoint
    this.selectedPsseVersion = version;
    this.cdr.detectChanges();
  }

  onStopWorkflow(): void {
    if (!this.threadId) return;
    this.chatService.stopWorkflow(this.threadId).pipe(
      tap(() => {
        this.isStreaming = false;
        const last = this.executeCommandList[this.executeCommandList.length - 1];
        if (last) {
          last.isStreaming = false;
          last.currentStreamingState = 'run_complete';
        }
        this.streamingService.disconnect();
        this.executeCommandList = [...this.executeCommandList];
        this.cdr.detectChanges();
        this.showToast('info', 'Stopped', 'Workflow has been stopped.');
      }),
      catchError(() => {
        this.isStreaming = false;
        this.streamingService.disconnect();
        this.cdr.detectChanges();
        return of(null);
      })
    ).subscribe();
  }

  resetStreamData(): void {
    this.finalCode = '';
    this.isStreaming = false;
    this.insightsContent = '';
    this.streamingData = null;
    this.isUserSubmitted = false;  // ← add this
  }

  ngOnDestroy() {
    this.streamingService.disconnect();
  }

}
